import { FormattedDate } from 'react-intl';

import type { EstimationAdditionalFieldResponse } from '@/api/schema/data-contracts';
import { SanitizedHtml } from '@/components/atoms/SanitizedHtml';
import { ProjectInfoMessages } from '@/components/organism/NewEstimation/ProjectBasics/ProjectInfo/messages';
import { useMessages } from '@/hooks/messages';
import { isHttpUrl } from '@/utils/url';

import messages from '../messages';

type FieldDefinition = { id: string; name: string; order: number; additionalFieldType: string };

export interface BasicProjectDataProps {
  projectDescription: string | null | undefined;
  additionalFieldDefinitions: FieldDefinition[] | null | undefined;
  additionalFields: EstimationAdditionalFieldResponse[] | null | undefined;
  hiddenFields: string[];
}

const PROJECT_DESCRIPTION_FIELD_ID = 'projectDescription';

export const BasicProjectData = ({
  projectDescription,
  additionalFieldDefinitions,
  additionalFields,
  hiddenFields,
}: BasicProjectDataProps) => {
  const managementReportMessages = useMessages(messages);
  const projectInfoMessages = useMessages(ProjectInfoMessages);

  const shouldShowProjectDescription =
    projectDescription &&
    projectDescription.trim() !== '' &&
    !hiddenFields.includes(PROJECT_DESCRIPTION_FIELD_ID);

  let optionalFieldsData: Array<{ definition: FieldDefinition; value: string | null }> = [];

  if (additionalFieldDefinitions && additionalFields) {
    const fieldsData = additionalFieldDefinitions
      .slice()
      .sort((a, b) => a.order - b.order)
      .map((fieldDefinition) => {
        const fieldValue = additionalFields.find(
          (field) => field.estimationModelAdditionalFieldDefinitionId === fieldDefinition.id,
        );
        return {
          definition: fieldDefinition,
          value: fieldValue?.value || null,
        };
      });

    optionalFieldsData = fieldsData.filter(
      (item) =>
        item.value !== null && item.value !== '' && !hiddenFields.includes(item.definition.id),
    );
  }

  const hasOptionalFields = optionalFieldsData.length > 0;

  if (!shouldShowProjectDescription && !hasOptionalFields) return null;

  return (
    <div className='mb-6'>
      <h2 className='mb-3'>{managementReportMessages.basicProjectData()}</h2>
      {/* TODO: replace bg-gray-100 with project palette token (EWM follow-up) */}
      <div className='dark:bg-c16 grid grid-cols-1 gap-4 rounded-lg bg-gray-100 p-4 md:grid-cols-2 lg:grid-cols-3'>
        {/* Project description spans the full row */}
        {shouldShowProjectDescription && (
          <div className='space-y-1 md:col-span-2 lg:col-span-3'>
            <dt className='text-sm font-bold'>{projectInfoMessages.projectDescriptionLabel()}:</dt>
            <dd className='text-sm whitespace-pre-wrap'>{projectDescription}</dd>
          </div>
        )}
        {optionalFieldsData.map((field) => (
          <div key={field.definition.id} className='space-y-1'>
            <dt className='text-sm font-bold'>{field.definition.name}:</dt>
            <dd className='w-2/3 text-sm break-words'>
              {field.definition.additionalFieldType === 'FORMATTED_TEXT' ? (
                <SanitizedHtml htmlString={field.value as string} />
              ) : field.definition.additionalFieldType === 'DATE' ? (
                <FormattedDate value={new Date(field.value as string)} />
              ) : field.definition.additionalFieldType === 'DOCUMENT_LINK' &&
                typeof field.value === 'string' &&
                isHttpUrl(field.value) ? (
                <a
                  href={field.value}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='text-blue-400 underline hover:text-blue-300'
                >
                  {field.value}
                </a>
              ) : (
                field.value
              )}
            </dd>
          </div>
        ))}
      </div>
    </div>
  );
};
